import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useState } from 'react';

import { NovaScheduleCreateView } from './nova-schedule-create-view';
import { NovaSchedulesList } from './nova-schedules-list';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../../lib/nova-schedule-proposal';
import { GET_NOVA_SCHEDULES, isNovaScheduleRecord } from '../../../../queries/nova-schedule-management';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useSettingsPanelContext } from '../settings-panel-context';

const MANAGEABLE_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'COMPLETED'] as const;

export function NovaSchedulesSettings() {
  const schedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const { userUuid, setIsSettingsOpen, startNewChatWithInput } = useChatOverlayContext();
  const { pushView } = useSettingsPanelContext();
  const [isCreating, setIsCreating] = useState(false);
  const { data, loading, error, refetch } = useQuery(GET_NOVA_SCHEDULES, {
    variables: {
      filters: {
        ownerUserUuids: [userUuid],
        statuses: [...MANAGEABLE_SCHEDULE_STATUSES],
      },
    },
    skip: !schedulesEnabled || !userUuid,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  if (!schedulesEnabled) return null;

  const schedules = data?.getNovaSchedules.filter(isNovaScheduleRecord) ?? [];
  const showCreateView = isCreating || (!loading && !error && schedules.length === 0);

  if (showCreateView) {
    return (
      <NovaScheduleCreateView
        showBack={schedules.length > 0}
        onBack={() => setIsCreating(false)}
        onSubmit={(prompt) => {
          setIsSettingsOpen(false);
          startNewChatWithInput(prompt);
        }}
      />
    );
  }

  return (
    <NovaSchedulesList
      schedules={schedules}
      isLoading={loading && data === undefined}
      error={error}
      onNewSchedule={() => setIsCreating(true)}
      onRetry={() => void refetch()}
      onSelectSchedule={(scheduleUuid) => pushView({ type: 'nova-schedule-detail', scheduleUuid })}
    />
  );
}
